/**
 * useSearchResults Hook
 *
 * Fetches and filters listing results from Firestore.
 */

import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import type { SearchFilters, SearchSort } from './useSearchFilters';

export interface ListingResult {
  id: string;
  title: string;
  photos: Array<{ url: string; caption?: string }>;
  propertyType: string;
  location: {
    city: string;
    area: string;
    country: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  pricing: {
    nightlyRate: number;
    currency: string;
  };
  rating?: number;
  reviewCount?: number;
  likeCount?: number;
  bedrooms: number;
  beds: number;
  bathrooms: number;
  maxGuests: number;
  amenityIds: string[];
  instantBooking: boolean;
  petFriendly: boolean;
  isFeatured?: boolean;
  hostId?: string;
  host?: {
    displayName: string;
    photoURL?: string;
  };
}

interface UseSearchResultsReturn {
  listings: ListingResult[];
  isLoading: boolean;
  error: Error | null;
  totalCount: number;
  hasMore: boolean;
  loadMore: () => void;
}

export function useSearchResults(
  filters: SearchFilters,
  sort: SearchSort
): UseSearchResultsReturn {
  const [allListings, setAllListings] = useState<ListingResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch listings from Firestore on mount
  useEffect(() => {
    async function fetchListings() {
      setIsLoading(true);
      try {
        const listingsRef = collection(db, 'listings');
        // Order by createdAt, NOT rating.average: a Firestore orderBy silently
        // excludes documents that lack the field, so ordering by rating.average
        // dropped every listing with no reviews yet — a brand-new approved
        // listing never appeared in search. Every listing has createdAt.
        const q = query(listingsRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);

        const listings: ListingResult[] = snapshot.docs
          // Only published listings are publicly searchable (drafts/pending out).
          .filter((doc) => (doc.data().status ?? 'published') === 'published')
          // Hide listings whose migrated host has not retrieved their account.
          // In-app listings and retrieved migrated hosts have hostClaimed:true.
          .filter((doc) => doc.data().hostClaimed === true)
          .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || '',
            photos: data.photos || [],
            propertyType: data.propertyType || 'apartment',
            location: {
              city: data.location?.city || 'Lebanon',
              area: data.location?.state || 'Lebanon',
              country: data.location?.country || 'Lebanon',
              coordinates: data.location?.coordinates
                ? {
                    latitude: data.location.coordinates.lat,
                    longitude: data.location.coordinates.lng,
                  }
                : undefined,
            },
            pricing: {
              nightlyRate: data.pricing?.nightlyRate || 100,
              currency: data.pricing?.currency || 'USD',
            },
            rating: data.rating?.average > 0 ? data.rating.average : undefined,
            reviewCount: data.rating?.count > 0 ? data.rating.count : undefined,
            likeCount: data.likeCount > 0 ? data.likeCount : undefined,
            bedrooms: data.capacity?.bedrooms || 1,
            beds: data.capacity?.beds || 1,
            bathrooms: data.capacity?.bathrooms || 1,
            maxGuests: data.capacity?.guests || 2,
            amenityIds: data.amenities || [],
            instantBooking: data.instantBook || false,
            petFriendly: data.petFriendly || false,
            isFeatured: data.isFeatured || false,
            hostId: data.hostId || '',
            host: data.host ? {
              displayName: data.host.displayName || '',
              photoURL: data.host.photoURL,
            } : undefined,
          };
        });

        setAllListings(listings);
        setError(null);
      } catch (err) {
        console.error('Error fetching listings:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch listings'));
      } finally {
        setIsLoading(false);
      }
    }

    fetchListings();
  }, []);

  // Apply filters and sort to listings
  const filteredListings = useMemo(() => {
    let results = [...allListings];

    // Location filter (search query)
    if (filters.location) {
      const locationLower = filters.location.toLowerCase();
      results = results.filter(
        (l) =>
          l.title.toLowerCase().includes(locationLower) ||
          l.location.city.toLowerCase().includes(locationLower) ||
          l.location.area.toLowerCase().includes(locationLower)
      );
    }

    // Guest capacity
    const totalGuests = filters.guests.adults + filters.guests.children;
    if (totalGuests > 0) {
      results = results.filter((l) => l.maxGuests >= totalGuests);
    }

    // Property type
    if (filters.propertyTypes.length > 0) {
      results = results.filter((l) =>
        filters.propertyTypes.includes(l.propertyType)
      );
    }

    // Bedrooms
    if (filters.bedrooms) {
      results = results.filter((l) => l.bedrooms >= filters.bedrooms!);
    }

    // Bathrooms
    if (filters.bathrooms) {
      results = results.filter((l) => l.bathrooms >= filters.bathrooms!);
    }

    // Price range
    if (filters.priceMin) {
      results = results.filter((l) => l.pricing.nightlyRate >= filters.priceMin!);
    }
    if (filters.priceMax) {
      results = results.filter((l) => l.pricing.nightlyRate <= filters.priceMax!);
    }

    // Amenities
    if (filters.amenities.length > 0) {
      results = results.filter((l) =>
        filters.amenities.every((a) => l.amenityIds.includes(a))
      );
    }

    // Instant booking
    if (filters.instantBooking) {
      results = results.filter((l) => l.instantBooking);
    }

    // Pet friendly
    if (filters.petFriendly) {
      results = results.filter((l) => l.petFriendly);
    }

    // Sort
    results.sort((a, b) => {
      switch (sort.field) {
        case 'price':
          return sort.direction === 'asc'
            ? a.pricing.nightlyRate - b.pricing.nightlyRate
            : b.pricing.nightlyRate - a.pricing.nightlyRate;
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'createdAt':
          return 0; // Already sorted by newest from Firestore
        default:
          // Featured first, then by rating
          if (a.isFeatured && !b.isFeatured) return -1;
          if (!a.isFeatured && b.isFeatured) return 1;
          return (b.rating || 0) - (a.rating || 0);
      }
    });

    return results;
  }, [allListings, filters, sort]);

  return {
    listings: filteredListings,
    isLoading,
    error,
    totalCount: filteredListings.length,
    hasMore: false,
    loadMore: () => {},
  };
}

export default useSearchResults;
